import type { Status1, Conflict1 } from '@mm-shared/types';
import type { Objective1 } from '@mm-shared/types/Objective1';
import type { Result1 } from '@mm-shared/types/Result1';
import type { Comment1 } from '@mm-shared/types/Comment1';
import { filterItemsForInterval } from '@mm-shared/intervals';

/**
 * Helper function to check if two conflicts involve the same parties
 */
function haveSameParties(a: Conflict1, b: Conflict1): boolean {
  // Match on the core identifiers without maps. Technically, the same
  // "parties" to this conflict could be on multiple maps. But it's still
  // the same conflict. So, less is more here, leaving out the maps.
  return (
    a.partiesUuids.sourceObjective === b.partiesUuids.sourceObjective &&
    a.partiesUuids.sourceResult === b.partiesUuids.sourceResult &&
    a.partiesUuids.targetObjective === b.partiesUuids.targetObjective &&
    a.partiesUuids.targetResult === b.partiesUuids.targetResult
  );
}

/**
 * Updates a status record when a conflict is detected
 */
export function updateStatusWithConflict(status: Status1, conflict: Conflict1): Status1 {
  // Check if this conflict already exists
  const existingConflict = (status.knownConflicts || []).find((known) =>
    haveSameParties(known, conflict),
  );

  if (existingConflict) {
    // If it exists, don't add it again
    return status;
  }

  const isFuture = new Date(status.forIntervalEnding) > new Date();

  return {
    ...status,
    // TODO review/revise. It should really be 'R' for milestone, 'r' for inchstone.
    // Only update the status code if the date is in the future. Otherwise, assume
    // the status was already set by users to whatever its supposed to be.
    statusCode: isFuture ? 'r' : status.statusCode,
    knownConflicts: [...(status.knownConflicts || []), conflict],
    lastUpdatedDate: new Date().toISOString(),
  };
}

/**
 * Updates a status record when a conflict is resolved
 */
export function updateStatusWithResolvedConflict(
  status: Status1,
  resolvedConflict: Conflict1,
): Status1 {
  // Remove matching conflict from knownConflicts and add to resolvedConflicts
  const newKnownConflicts = (status.knownConflicts || []).filter(
    (known) => !haveSameParties(known, resolvedConflict),
  );

  const isFuture = new Date(status.forIntervalEnding) > new Date();

  return {
    ...status,
    // Only change status back if there are no remaining conflicts AND the
    // date is in the future. (If the date has passed, leave it as-is. This is
    // probably reflective of however the user chose to record it.)
    statusCode: isFuture ? 'p' : status.statusCode,
    knownConflicts: newKnownConflicts,
    resolvedConflicts: [...(status.resolvedConflicts || []), resolvedConflict],
    lastUpdatedDate: new Date().toISOString(),
  };
}

/**
 * Checks if a status has any active conflicts
 */
export function hasActiveConflicts(status: Status1): boolean {
  return (status.knownConflicts || []).length > 0;
}

// --- Presentation helpers moved from StatusCell1.tsx ---

export function getBackgroundColor(
  statusCode: string,
  hasResults = false,
  hasKeyResults = false,
  mode = 'traditional',
): string {
  let backgroundColor = '';

  // First check basic status codes (g, y, r, p, ?).
  switch (statusCode) {
    case 'g':
      backgroundColor = 'bg-status-g';
      break;
    case 'r':
      backgroundColor = 'bg-status-r';
      break;
    case 'y':
      backgroundColor = 'bg-status-y';
      break;
    case 'p':
      backgroundColor = 'bg-status-p';
      break;
    case '?':
      backgroundColor = 'bg-status-tbd';
      break;
    case '':
      backgroundColor = 'bg-gray-50';
      break;
    default:
      throw new Error(`Invalid status code: ${statusCode}`);
  }

  // Traditional mode. Handle inchstone and milestone statuses
  // consistent with spreadsheet-based milestone maps through verion 4 for:
  // i, M, G, Y, R.
  let traditionalCode = '';

  // Handle inchstones and milestones in order of specificity
  if (statusCode === 'p') {
    if (hasResults && !hasKeyResults) {
      traditionalCode = 'i'; // Inchstone
    } else if (hasKeyResults) {
      traditionalCode = 'M'; // Milestone
    }
  }

  // Handle completed milestone statuses
  if (hasKeyResults) {
    if (statusCode === 'g') traditionalCode = 'G';
    if (statusCode === 'y') traditionalCode = 'Y';
    if (statusCode === 'r') traditionalCode = 'R';
  }

  // TODO: Revisit color palette and style.
  // At least add more contrast. Maybe change styling
  // all together, add svgs, or something fancy.
  switch (traditionalCode) {
    case 'G':
      backgroundColor = 'bg-status-G';
      break;
    case 'R':
      backgroundColor = 'bg-status-R';
      break;
    case 'Y':
      backgroundColor = 'bg-status-Y';
      break;
    case 'i':
      backgroundColor = 'bg-status-i';
      break;
    case 'M':
      backgroundColor = 'bg-status-M';
      break;
  }

  return backgroundColor;
}

export function getTriangleIndicatorClass(
  corner: 'topRight' | 'bottomRight',
  color: string,
): string {
  const cornerPositions = {
    topRight: 'before:top-0 before:right-0',
    bottomRight: 'after:bottom-0 after:right-0',
  };

  const colorClasses = {
    'red-500': {
      bottom: 'after:border-b-red-500 after:border-r-red-500',
      top: 'before:border-t-red-500 before:border-r-red-500',
    },
    'blue-500': {
      bottom: 'after:border-b-blue-500 after:border-r-blue-500',
      top: 'before:border-t-blue-500 before:border-r-blue-500',
    },
    'gray-100': {
      bottom: 'after:border-b-gray-100 after:border-r-gray-100',
      top: 'before:border-t-gray-100 before:border-r-gray-100',
    },
    'gray-200': {
      bottom: 'after:border-b-gray-200 after:border-r-gray-200',
      top: 'before:border-t-gray-200 before:border-r-gray-200',
    },
    'gray-300': {
      bottom: 'after:border-b-gray-300 after:border-r-gray-300',
      top: 'before:border-t-gray-300 before:border-r-gray-300',
    },
    'gray-400': {
      bottom: 'after:border-b-gray-400 after:border-r-gray-400',
      top: 'before:border-t-gray-400 before:border-r-gray-400',
    },
    'gray-500': {
      bottom: 'after:border-b-gray-500 after:border-r-gray-500',
      top: 'before:border-t-gray-500 before:border-r-gray-500',
    },
    'gray-900': {
      bottom: 'after:border-b-gray-900 after:border-r-gray-900',
      top: 'before:border-t-gray-900 before:border-r-gray-900',
    },
    'white-bordered': {
      bottom: 'after:border-b-white after:border-r-white',
      top: 'before:border-t-white before:border-r-white',
    },
    'status-G': {
      bottom: 'after:border-b-status-G after:border-r-status-G',
      top: 'before:border-t-status-G before:border-r-status-G',
    },
    // Add other colors as needed
  };

  const pseudoElement = corner === 'topRight' ? 'before' : 'after';

  return `relative ${cornerPositions[corner]} ${pseudoElement}:absolute ${pseudoElement}:w-0 ${pseudoElement}:h-0 ${
    corner === 'bottomRight'
      ? `after:border-b-8 after:border-r-8 ${colorClasses[color as keyof typeof colorClasses].bottom} after:border-l-8 after:border-l-transparent after:border-t-8 after:border-t-transparent`
      : `before:border-t-8 before:border-r-8 ${colorClasses[color as keyof typeof colorClasses].top} before:border-l-transparent before:border-l-8 before:border-b-transparent before:border-b-8`
  }`;
}

/**
 * Note: This is funny because it's telling us things we need to know
 * about a status cell, but not looking at the corresponding status record.
 * It seems like statusCode, hasConflict, etc might all be natural details to
 * return to. Review/Revise as this evolves.
 *
 * Returns { hasResults, hasKeyResults, hasComments } for a given cell
 */
export function getStatusCellState(
  objective: Objective1,
  interval: { start: Date; end: Date },
): { hasResults: boolean; hasKeyResults: boolean; hasComments: boolean } {
  const resultsForInterval = filterItemsForInterval<Result1>(
    objective.results,
    interval.start,
    interval.end,
  );
  const hasResults = resultsForInterval.length > 0;
  const hasKeyResults = resultsForInterval.some((r: Result1) => r.isKeyResult);

  // Use interval filtering for comments too, instead of exact string matching
  const commentsForInterval = filterItemsForInterval(
    objective.comments || [],
    interval.start,
    interval.end,
  );
  const hasComments = commentsForInterval.length > 0;

  return { hasResults, hasKeyResults, hasComments };
}

export function getBackgroundColorForResultCircles(
  statusCode: string,
  hasResults = false,
  hasKeyResults = false,
): string {
  // Use default logic first
  const backgroundColor = getBackgroundColor(statusCode, hasResults, hasKeyResults);

  // For inchstones, background should be the same as planned work.
  // The inchstones (result circles) will be darker and indicate
  // that there's an inchstone here.
  if (statusCode === 'p' && hasResults && !hasKeyResults) {
    return 'bg-status-p';
  }

  return backgroundColor;
}
